<template>
  <section class="coaching-brief-view">
    <AskReportChips :report-view="reportView" />

    <section
      v-for="section in sections"
      :key="section.title"
      class="mode-card mode-card--accordion"
    >
      <button
        class="accordion-header"
        type="button"
        :aria-expanded="isSectionExpanded(section.title)"
        @click="toggleSection(section.title)"
      >
        <span class="section-label">{{ section.title }}</span>
        <b-icon
          :icon="isSectionExpanded(section.title) ? 'chevron-down' : 'chevron-right'"
          size="is-small"
          class="accordion-chevron"
        />
      </button>

      <div v-show="isSectionExpanded(section.title)" class="accordion-body">
        <p
          v-if="section.block.type === 'text'"
          class="mode-text"
          v-html="linkifyTicketRefs(section.block.text)"
          @click="onItemClick"
        />

        <ul v-else-if="section.block.type === 'list'" class="mode-list">
          <li
            v-for="(item, index) in section.block.items"
            :key="`${section.title}-${index}`"
            v-html="linkifyTicketRefs(item)"
            @click="onItemClick"
          />
        </ul>

        <dl v-else-if="section.block.type === 'pairs'" class="mode-pairs">
          <div
            v-for="pair in section.block.pairs"
            :key="`${section.title}-${pair.label}`"
            class="mode-pairs__item"
          >
            <dt>{{ pair.label }}</dt>
            <dd>{{ pair.value }}</dd>
          </div>
        </dl>
      </div>
    </section>
  </section>
</template>

<script>
import { ticketRefClass } from '@/utils/visitedTickets'
import AskReportChips from '@/components/roster/ask/AskReportChips.vue'

export default {
  name: 'CoachingBriefView',
  components: {
    AskReportChips,
  },
  props: {
    reportView: {
      type: Object,
      default: () => null,
    },
    signalStrength: {
      type: Object,
      default: () => ({}),
    },
    sections: {
      type: Array,
      default: () => [],
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  data() {
    return {
      expandedSections: {},
    }
  },
  methods: {
    isSectionExpanded(title) {
      return this.expandedSections[title] !== false
    },
    toggleSection(title) {
      this.expandedSections = {
        ...this.expandedSections,
        [title]: !this.isSectionExpanded(title),
      }
    },
    linkifyTicketRefs(text) {
      if (!text || !this.ticketRefs.length) return this.escapeHtml(text)
      const ticketNbrs = this.ticketRefs.map((t) => t.ticketNbr).filter(Boolean)
      if (!ticketNbrs.length) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const pattern = new RegExp(`\\b(${ticketNbrs.map(this.escapeRegex).join('|')})\\b`, 'g')
      return escaped.replace(
        pattern,
        (match) => `<span class="${ticketRefClass(match)}" data-ticket-nbr="${match}" role="button" tabindex="0">${match}</span>`
      )
    },
    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    onItemClick(event) {
      const ticketNbr = event.target?.dataset?.ticketNbr
      if (ticketNbr) {
        this.$emit('ticket-ref-clicked', ticketNbr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.coaching-brief-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
}

.mode-card--accordion {
  overflow: hidden;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: $space-3;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  gap: $space-2;

  &:hover {
    background: $color-surface-alt;
  }
}

.accordion-body {
  padding: 0 $space-3 $space-3;
}

.accordion-chevron {
  flex-shrink: 0;
  color: $color-text-muted;
}

.section-label,
.mode-pairs__item dt {
  margin: 0 0 $space-1;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.accordion-header .section-label {
  margin: 0;
}

.mode-text {
  margin: 0;
  white-space: pre-line;
  line-height: 1.55;
}

.mode-list {
  margin: 0;
  padding-left: 1.2rem;
  list-style-type: disc;
}

.mode-list li + li {
  margin-top: $space-2;
}

.mode-pairs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: 0;
}

.mode-pairs__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.mode-pairs__item dd {
  margin: 0;
  line-height: 1.45;
}

:deep(.ticket-ref) {
  color: $color-primary-dark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }

  &.ticket-ref--visited {
    color: $color-text-muted;
  }
}

@media (max-width: 1100px) {
  .mode-pairs {
    grid-template-columns: 1fr;
  }
}
</style>
